import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_ui } from '@/hooks/store/use_slices';
import { ENUM__image_brush, ENUM_UTILS__image_brush } from '@/pages/page_generation/layouts/assets_images/_base/ENUM__image_brush';
import { CALLBACK_ASYNC__canvas } from '@/types/types';
import { path_relative_to_absolute } from '@/utils/utils_files_client';
import { ENUM__color } from '@shared/dto/+image/_enums/ENUM__color';
import { DTO__image_asset } from '@shared/dto/+image/image_asset_base/DTO__image_asset';
import { DTO__image_size } from '@shared/dto/+image/image_asset_base/DTO__image_size';
import p5 from 'p5';
import { useEffect, useRef } from 'react';
import { HOOK_CANVAS__draw_image_into_canvas } from '../HOOK_CANVAS__draw_image_into_canvas';

interface PROPS {
  div_element: HTMLDivElement;
  container_size: DTO__image_size;
  image: DTO__image_asset;
  callback__apply_changes: CALLBACK_ASYNC__canvas;
}

export function HOOK_CANVAS__p5({ div_element, image, container_size, callback__apply_changes }: PROPS) {
  /*
   * STORE
   */
  const { g_image_edit__brush, g_general__ui_disabled } = use_slice_ui(['g_image_edit__brush', 'g_general__ui_disabled']);

  const ref_canvas = useRef(ENUM__html_ref_stub.CANVAS);
  const ref_p5_instance = useRef<p5 | null>(null);
  const ref_is_drawing = useRef(false);

  const { g_image_edit__brush_size } = use_slice_ui(['g_image_edit__brush_size', 'g_image_edit__brush']);

  const { draw_image_into_canvas } = HOOK_CANVAS__draw_image_into_canvas();

  /*
   * STATE
   */

  const ref_brush_color = useRef(ENUM_UTILS__image_brush.get_brush_color(g_image_edit__brush));
  const ref_brush_size = useRef(g_image_edit__brush_size);

  /*
   * EFFECTS
   */

  useEffect(() => {
    void draw_image_into_canvas(ref_canvas.current, image);
  }, [image]);

  useEffect(() => {
    if (g_image_edit__brush === ENUM__image_brush.POINTER) return;
    if (g_general__ui_disabled) return;

    void create_p5_instance();

    return () => destroy_p5_instance();
  }, [container_size, g_image_edit__brush, g_general__ui_disabled]);

  use_effect(() => {
    ref_brush_size.current = g_image_edit__brush_size;
  }, [g_image_edit__brush_size]);

  use_effect(() => {
    ref_brush_color.current = ENUM_UTILS__image_brush.get_brush_color(g_image_edit__brush);
  }, [g_image_edit__brush]);

  /*
   * EVENTS
   */

  function onMousePressedInternal(p: p5, e: any) {
    if (p.mouseButton !== p.LEFT) return;
    if (g_image_edit__brush === ENUM__image_brush.POINTER) return;

    if (!is_inside_canvas(p)) return;

    ref_is_drawing.current = true;

    draw_point(p);
  }

  function onMouseDraggedInternal(p: p5) {
    if (p.mouseButton !== p.LEFT) return;
    if (g_image_edit__brush === ENUM__image_brush.POINTER) return;

    if (!is_inside_canvas(p)) return;
    if (!ref_is_drawing.current) return;

    draw_line(p);
  }

  function onMouseReleased(p: p5) {
    if (!ref_is_drawing.current) return;

    ref_is_drawing.current = false;

    void callback__apply_changes(ref_canvas);
  }

  /*
   * METHODS
   */

  async function create_p5_instance() {
    if (div_element == ENUM__html_ref_stub.DIV) return;
    if (image.is_stub()) return;
    if (container_size.is_zero()) return;

    destroy_p5_instance();

    ref_p5_instance.current = new p5(init_p5);
  }

  function destroy_p5_instance() {
    if (!ref_p5_instance.current) return;

    if (ref_canvas.current != ENUM__html_ref_stub.CANVAS) {
      ref_canvas.current.remove();
      ref_canvas.current = ENUM__html_ref_stub.CANVAS;
    }

    ref_p5_instance.current.mousePressed = () => {};
    ref_p5_instance.current.mouseDragged = () => {};
    ref_p5_instance.current.mouseReleased = () => {};
    ref_p5_instance.current.remove();
  }

  function init_p5(p: p5) {
    p.preload = () => {
      const url_absolute = path_relative_to_absolute(image.url_relative);
      p.loadImage(url_absolute);
    };

    p.setup = () => {
      let renderer: p5.Renderer = p.createCanvas(container_size.width, container_size.height);
      renderer.parent(div_element);

      renderer.addClass('info_class_p5_renderer absolute top-0 opacity-20 cursor-none');

      // 2. Disable right-click context menu on the canvas element
      renderer.elt.addEventListener('contextmenu', (e: MouseEvent) => e.preventDefault());

      ref_canvas.current = renderer.elt;

      p.pixelDensity(1);

      p.background(ENUM__color.WHITE);

      void draw_image_into_canvas(ref_canvas.current, image);
    };

    p.mousePressed = (e: any) => onMousePressedInternal(p, e);
    p.mouseDragged = () => onMouseDraggedInternal(p);
    p.mouseReleased = () => onMouseReleased(p);
  }

  function is_inside_canvas(p: p5) {
    if (ref_canvas.current == ENUM__html_ref_stub.CANVAS) return false;
    if (p.width == 0 || p.height == 0) return false;

    let out = p.mouseX >= 0 && p.mouseY >= 0 && p.mouseX < p.width && p.mouseY < p.height;

    return out;
  }

  function draw_point(p: p5) {
    p.noSmooth();
    p.noStroke();
    p.fill(ref_brush_color.current);

    let { mouseX, mouseY } = p;
    [mouseX, mouseY] = [Math.round(mouseX), Math.round(mouseY)];

    p.ellipse(mouseX, mouseY, ref_brush_size.current, ref_brush_size.current);
  }

  function draw_line(p: p5) {
    p.noSmooth();
    p.stroke(ref_brush_color.current);
    p.strokeWeight(ref_brush_size.current);
    p.strokeCap(p.ROUND);

    let { mouseX, mouseY, pmouseX, pmouseY } = p;
    [mouseX, mouseY, pmouseX, pmouseY] = [Math.round(mouseX), Math.round(mouseY), Math.round(pmouseX), Math.round(pmouseY)];

    p.line(pmouseX, pmouseY, mouseX, mouseY);
  }

  return ref_canvas;
}
